import { LearningModuleContentsEntry, ModuleTableOfContents } from 'types/learningHub/moduleContents';
import './ContentHeader.scss';
import {
  getCertificationDescription,
  getLessonHeader,
  isCertificationEntry,
  isChapterTestEntry,
  isLessonEntry,
} from 'utilities/learningModuleHelpers';
import { Subtitle } from 'components/TextComps/Subtitle';
import { Paragraph } from 'components/TextComps/Paragraph';
import { CertificationTest } from 'types/module';
import { ReactComponent as Crown } from 'images/icons/Crown.svg';
import { ReactComponent as Medal } from 'images/icons/MedalThin.svg';
import { ArrowNavigationButton } from 'components/ArrowNavigationButton/ArrowNavigationButton';
import Button from '@trackman/web-shared-components/components/Button/Button';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronLeft } from 'images/icons/ChevronLeftThin.svg';

type Props = {
  selectedEntry: LearningModuleContentsEntry;
  tableOfContents: ModuleTableOfContents;
  isUserMember: boolean | undefined;
  navigationDisabled?: boolean;
  handleSelectedEntry: (newlySelectedEntry: LearningModuleContentsEntry) => void;
  certificationTests?: CertificationTest[];
};

export const ContentHeader = ({
  selectedEntry,
  tableOfContents,
  isUserMember,
  handleSelectedEntry,
  certificationTests,
  navigationDisabled,
}: Props) => {
  const { t } = useTranslation();
  const entryHeader = getLessonHeader(tableOfContents?.chapters ?? [], selectedEntry?.id ?? '');

  return (
    <div className='learning-module-content-header'>
      {isLessonEntry(selectedEntry) && (
        <div className='title-wrapper'>
          <Subtitle>
            <>{entryHeader.header}</>
          </Subtitle>
          <Paragraph>
            <>{entryHeader.subheader}</>
          </Paragraph>
        </div>
      )}
      {isCertificationEntry(selectedEntry) && (
        <div className='title-wrapper'>
          <p className='subtitle'>{t('CertificationExam', { ns: 'learningHub' })}</p>
          <div className='certificate-title'>
            {isUserMember ? selectedEntry.status === 'Completed' ? <></> : <Medal /> : <Crown className='premium-test' />}
            <Subtitle>
              <>{selectedEntry?.title}</>
            </Subtitle>
          </div>
          {selectedEntry?.status === 'Completed' ? (
            <></>
          ) : (
            <Paragraph>
              <>{getCertificationDescription(certificationTests ?? [], selectedEntry?.id ?? '')}</>
            </Paragraph>
          )}
        </div>
      )}
      {isChapterTestEntry(selectedEntry) && (
        <div className='title-wrapper'>
          <p className='subtitle'>{t('ChapterTest', { ns: 'learningHub' })}</p>
          <div className='certificate-title'>
            {isUserMember ? <></> : <Crown className='premium-test' />}
            <Subtitle>
              <>{selectedEntry?.title}</>
            </Subtitle>
          </div>
        </div>
      )}
      <ModuleNavigation
        navigationDisabled={navigationDisabled ?? false}
        selectedEntry={selectedEntry}
        tableOfContents={tableOfContents}
        handleSelectedEntry={handleSelectedEntry}
      />
    </div>
  );
};

const ModuleNavigation = ({
  navigationDisabled,
  selectedEntry,
  tableOfContents,
  handleSelectedEntry,
}: {
  navigationDisabled: boolean;
  selectedEntry: LearningModuleContentsEntry;
  tableOfContents: ModuleTableOfContents;
  handleSelectedEntry: (newlySelectedEntry: LearningModuleContentsEntry) => void;
}) => {
  const { t } = useTranslation();
  const availableLessons = tableOfContents?.chapters.flatMap((chapter) =>
    chapter.lessons
      .filter((lesson) => lesson.status !== 'Locked' && lesson.status !== 'ComingSoon')
      .map((lesson) => ({ ...lesson, chapterId: chapter.id }))
  );
  const availableCertifications = tableOfContents?.certifications.filter(
    (certification) => certification.status !== 'Locked' && certification.status !== 'ComingSoon'
  );

  const availableEntries = [...availableLessons, ...availableCertifications];

  const getSelectedEntry = (direction: 'next' | 'prev'): LearningModuleContentsEntry | undefined => {
    const currentIndex = availableEntries.findIndex((entry) => {
      if ('chapterId' in entry && 'chapterId' in selectedEntry) {
        return entry.chapterId === selectedEntry?.chapterId && entry.id === selectedEntry?.id;
      }

      return entry.id === selectedEntry?.id;
    });

    if (currentIndex === -1) {
      return undefined;
    } else if (direction === 'next') {
      return availableEntries[currentIndex + 1];
    } else {
      return availableEntries[currentIndex - 1];
    }
  };

  const isLastLessonSelected = (): boolean => {
    const allLessons = availableLessons ?? [];
    return selectedEntry?.id === allLessons[allLessons.length - 1]?.id;
  };

  const isLastCertificationSelected = (): boolean => {
    const allCertifications = availableCertifications ?? [];
    return selectedEntry?.id === allCertifications[allCertifications.length - 1]?.id;
  };

  const isNextEntryCertificate = (): boolean => {
    const allCertifications = availableCertifications ?? [];
    const currentIndex = allCertifications.findIndex((entry) => entry.id === selectedEntry?.id);
    const nextIndex = currentIndex + 1;
    return (
      allCertifications.length > 0 &&
      ((currentIndex !== -1 && currentIndex < allCertifications.length - 1) || isLastLessonSelected()) &&
      nextIndex < allCertifications.length
    );
  };

  const getCertificationTestButtonLabel = (): string => {
    if (availableCertifications?.length === 1) {
      return t('TakeCertificationTest', { ns: 'learningHub' });
    } else {
      if (isLastLessonSelected()) {
        return t('TakeFirstCertificationTest', { ns: 'learningHub' });
      } else {
        return t('TakeNextCertificationTest', { ns: 'learningHub' });
      }
    }
  };

  return (
    <div className='module-navigation'>
      {availableCertifications?.length > 1 && isLastCertificationSelected() ? (
        <Button
          className='button previous-certification-button'
          variant='contained'
          onClick={() => handleSelectedEntry(getSelectedEntry('prev') ?? ({} as LearningModuleContentsEntry))}
          startIcon={<ChevronLeft />}
          disabled={navigationDisabled}
        >
          {t('PreviousCertificationTest', { ns: 'learningHub' })}
        </Button>
      ) : (
        <ArrowNavigationButton
          direction='prev'
          disabled={navigationDisabled || selectedEntry?.id === availableLessons?.[0]?.id}
          onClick={() => handleSelectedEntry(getSelectedEntry('prev') ?? ({} as LearningModuleContentsEntry))}
        />
      )}
      {isNextEntryCertificate() ? (
        <Button
          className='button'
          variant='contained'
          intent='primary'
          onClick={() => handleSelectedEntry(getSelectedEntry('next') ?? ({} as LearningModuleContentsEntry))}
          disabled={navigationDisabled}
        >
          {getCertificationTestButtonLabel()}
        </Button>
      ) : (
        <ArrowNavigationButton
          disabled={
            navigationDisabled || isLastCertificationSelected() || (isLastLessonSelected() && availableCertifications?.length === 0)
          }
          direction='next'
          onClick={() => handleSelectedEntry(getSelectedEntry('next') ?? ({} as LearningModuleContentsEntry))}
        />
      )}
    </div>
  );
};
